import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import Link from '@wix/thunderbolt-elements/components/Link';
import {
  DEFAULT_RADIUS,
  DEFAULT_TEXT_DIRECTION,
  SKIN_PARAM_RANGES,
  TestIds,
} from '../constants';
import type {
  TextParticlesImperativeActions,
  TextParticlesProps,
} from '../TextParticles.types';
import semanticClassNames from '../TextParticles.semanticClassNames';
import { useComputedStyle } from './hooks/useComputedStyle';
import { useImageLoader } from './hooks/useImageLoader';
import { useParticleRenderer } from './hooks/useParticleRenderer';
import { useWebWorker } from './hooks/useWebWorker';
import styles from './TextParticles.scss';
import { AnimationType, type AnimationState } from './TextParticles.types';
import { getCanvasDimensions } from './utils/canvas-utils';
import { getMainThreadMessages } from './worker/messages';

const TextParticles: React.ForwardRefRenderFunction<
  TextParticlesImperativeActions,
  TextParticlesProps
> = (props, ref) => {
  const {
    id,
    text,
    componentViewMode,
    isPresetPreview,
    reducedMotion: reducedMotionProp = false,
    className,
    customClassNames = [],
    tagName,
    textDirection = DEFAULT_TEXT_DIRECTION,
    link,
    onPlay,
    onStop,
    onMouseEnter,
    onMouseLeave,
    onPause,
  } = props;
  const SelectableTextElement = (tagName || 'p') as React.ElementType;
  const isEditor = componentViewMode === 'editor';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const computedStyle = useComputedStyle(id, rootRef, isEditor);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [showSelectableText, setShowSelectableText] = useState(true);
  const applyReducedMotion = (reducedMotion && !isEditor) || isPresetPreview;

  useEffect(() => {
    setReducedMotion(reducedMotionProp);
  }, [reducedMotionProp]);

  useEffect(() => {
    if (computedStyle?.animationType === AnimationType.OUT) {
      setShowSelectableText(true);
    }
  }, [computedStyle?.animationType]);

  const updateAnimationState = useCallback(
    (
      canvas: React.MutableRefObject<HTMLCanvasElement | null>,
      newAnimationState: AnimationState,
    ) => {
      if (canvas.current) {
        canvas.current.setAttribute(
          TestIds.viewer.animationState,
          newAnimationState,
        );
      }

      if (!rootRef.current) {
        return;
      }

      const hasDoneClass = rootRef.current.classList.contains(
        formatClassNames(semanticClassNames.rootDone),
      );

      if (
        hasDoneClass &&
        newAnimationState !== 'finished' &&
        newAnimationState !== 'paused'
      ) {
        rootRef.current.classList.remove(
          formatClassNames(semanticClassNames.rootDone),
        );
      }

      if (!hasDoneClass && newAnimationState === 'finished') {
        rootRef.current.classList.add(
          formatClassNames(semanticClassNames.rootDone),
        );
      }
    },
    [],
  );

  const canvasDimensions = useMemo(
    () =>
      getCanvasDimensions(
        computedStyle?.boundingBox,
        computedStyle?.spread ?? SKIN_PARAM_RANGES.spread.min,
      ),
    [computedStyle?.boundingBox, computedStyle?.spread],
  );
  const image = useImageLoader({ canvasDimensions, computedStyle, text });

  const worker = useWebWorker();

  const messages = useMemo(() => getMainThreadMessages(id), [id]);

  const onAnimationEnd = useCallback(() => {
    if (isEditor) {
      updateAnimationState(canvasRef, 'preview');
      worker?.postMessage(messages.getSetPreviewMessage());
    } else {
      updateAnimationState(canvasRef, 'finished');
    }

    if (computedStyle?.animationType === AnimationType.IN) {
      setShowSelectableText(true);
    }

    if (computedStyle?.animationType === AnimationType.OUT) {
      setShowSelectableText(false);
    }
  }, [
    worker,
    messages,
    isEditor,
    updateAnimationState,
    computedStyle?.animationType,
    setShowSelectableText,
  ]);

  const onAnimationStopped = useCallback(() => {
    if (isEditor) {
      updateAnimationState(canvasRef, 'preview');
      worker?.postMessage(messages.getSetPreviewMessage());
    } else {
      updateAnimationState(canvasRef, 'stopped');
    }

    if (
      !computedStyle?.animationType ||
      computedStyle.animationType === AnimationType.IN
    ) {
      setShowSelectableText(false);
    }

    if (computedStyle?.animationType === AnimationType.OUT) {
      setShowSelectableText(true);
    }
  }, [
    isEditor,
    computedStyle?.animationType,
    updateAnimationState,
    worker,
    messages,
  ]);

  const onInitialized = useCallback(() => {
    if (isEditor) {
      updateAnimationState(canvasRef, 'preview');
      worker?.postMessage(messages.getSetPreviewMessage());
    }
  }, [isEditor, updateAnimationState, worker, messages]);

  const { play, stop, pause } = useParticleRenderer({
    text,
    radius:
      DEFAULT_RADIUS *
      (canvasDimensions?.offscreenScale ?? SKIN_PARAM_RANGES.spread.min),
    duration: computedStyle?.duration ?? SKIN_PARAM_RANGES.duration.min,
    animationType: computedStyle?.animationType ?? AnimationType.IN,
    image,
    canvasRef,
    onAnimationEnd,
    componentId: id,
    worker,
    messages,
    direction: computedStyle?.animationDirection ?? 0,
    particleStartSize: computedStyle?.particleStartSize ?? 1,
    particleEndSize: computedStyle?.particleEndSize ?? 1,
    particleStartOpacity: computedStyle?.particleStartOpacity ?? 100,
    particleEndOpacity: computedStyle?.particleEndOpacity ?? 0,
    windDirection: computedStyle?.windDirection ?? 0,
    windSpeed: computedStyle?.windSpeed ?? 1,
    turbulence: computedStyle?.turbulence ?? 0.5,
    particleColor1: computedStyle?.particleColor1
      ? `rgb(${computedStyle?.particleColor1})`
      : 'rgb(0, 0, 0)',
    particleColor2: computedStyle?.particleColor2
      ? `rgb(${computedStyle?.particleColor2})`
      : 'rgb(0, 0, 0)',
    particleColor3: computedStyle?.particleColor3
      ? `rgb(${computedStyle?.particleColor3})`
      : 'rgb(0, 0, 0)',
    particleColor4: computedStyle?.particleColor4
      ? `rgb(${computedStyle?.particleColor4})`
      : 'rgb(0, 0, 0)',
    particleColorCount: computedStyle?.particleColorCount ?? 1,
    onInitialized,
    onAnimationStopped,
  });

  const handlePlay = useCallback(async () => {
    updateAnimationState(canvasRef, 'playing');
    onPlay?.({ type: 'onPlay', compId: id });
    if (computedStyle?.animationType === AnimationType.IN) {
      setShowSelectableText(false);
    }

    if (computedStyle?.animationType === AnimationType.OUT) {
      setShowSelectableText(true);
    }

    await play();
  }, [play, updateAnimationState, onPlay, id, computedStyle?.animationType]);

  const handlePause = useCallback(async () => {
    onPause?.({ type: 'onPause', compId: id });
    await pause();
    updateAnimationState(canvasRef, 'paused');
  }, [pause, updateAnimationState, onPause, id]);

  const handleStop = useCallback(async () => {
    onStop?.({ type: 'onStop', compId: id });
    await stop();
    updateAnimationState(canvasRef, 'stopped');
    onAnimationStopped();
  }, [onStop, id, stop, updateAnimationState, onAnimationStopped]);

  useImperativeHandle(
    ref,
    () => ({
      play: handlePlay,
      stop: handleStop,
      pause: handlePause,
    }),
    [handlePlay, handleStop, handlePause],
  );

  useEffect(() => {
    const canvas = canvasRef.current;

    if (isEditor || !canvas || !worker) {
      return;
    }

    const observer = new IntersectionObserver(
      async entries => {
        if (canvas.getAttribute(TestIds.viewer.animationState) === 'finished') {
          return;
        }

        for (const entry of entries) {
          if (entry.isIntersecting) {
            await handlePlay();
          } else {
            await handleStop();
          }
        }
      },
      { root: null, threshold: 0.5 },
    );

    observer.observe(canvas);

    return () => {
      observer.unobserve(canvas);
    };
  }, [isEditor, handlePlay, handleStop, worker]);

  useEffect(
    () => () => {
      worker?.postMessage(messages.getTerminateInstanceMessage());
    },
    [id, worker, messages],
  );

  return (
    <div
      id={id}
      ref={rootRef}
      className={classNames(
        styles.root,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      dir={textDirection}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {applyReducedMotion ? null : (
        <canvas
          aria-hidden={true}
          ref={canvasRef}
          className={classNames(styles.canvas, {
            [styles.spreadAreaBorder]:
              isEditor && computedStyle?.spreadAreaVisible,
          })}
          data-testid={TestIds.viewer.canvas}
          style={
            canvasDimensions
              ? {
                  width: canvasDimensions.displayWidth,
                  height: canvasDimensions.displayHeight,
                }
              : undefined
          }
        />
      )}
      {link ? (
        <Link
          {...link}
          className={styles.link}
          dataTestId={TestIds.viewer.link}
        >
          <SelectableTextElement
            ref={textRef}
            data-testid={TestIds.viewer.text}
            className={classNames(styles.selectableText, {
              [styles.reducedMotion]: applyReducedMotion,
              [styles.hidden]: !showSelectableText,
            })}
          >
            {text}
          </SelectableTextElement>
        </Link>
      ) : (
        <SelectableTextElement
          ref={textRef}
          data-testid={TestIds.viewer.text}
          className={classNames(styles.selectableText, {
            [styles.reducedMotion]: applyReducedMotion,
            [styles.hidden]: !showSelectableText,
          })}
        >
          {text}
        </SelectableTextElement>
      )}
    </div>
  );
};

export default React.forwardRef(TextParticles);
